import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Icons from '../Assets/icons/Icons';
import { formatDate } from '../Constants/constant';
import style from '../components/EachBlog/Each.module.css';

// Swiper imports
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Keyboard, Zoom } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

const BlogsDetail = () => {
  const location = useLocation();
  const blog = location.state;
  const [inView, setInView] = useState(false);

  // lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      (entries, obs) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    const galleryElement = document.querySelector(`.${style.galleryGrid}`);
    if (galleryElement) observer.observe(galleryElement);

    return () => observer.disconnect();
  }, []);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isLightboxOpen]);

  // Close on ESC
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setIsLightboxOpen(false);
  }, []);
  useEffect(() => {
    if (!isLightboxOpen) return;
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isLightboxOpen, onKeyDown]);

  if (!blog) return <p>Blog not found</p>;

  const openLightboxAt = (index) => {
    setStartIndex(index);
    setIsLightboxOpen(true);
  };

  return (
    <section className={style.blogDetail}>
      {/* Back Button */}
      <Link to="/blogs" className={style.back}>
        <span className={style.arrow}>
          <img src={Icons.arrowLeft} alt="Back arrow" className={style.icon} />
        </span>
      </Link>

      {/* Hero Image */}
      <div className={style.heroImage}>
        <img src={blog.image || blog.coverImage} alt={blog.title} />
        <span className={style.date}>{formatDate(blog.createdAt)}</span>
      </div>

      {/* Title & Intro */}
      <h2 className={style.blogTitle}>{blog.title}</h2>

      <div
        className={style.intro}
        dangerouslySetInnerHTML={{ __html: blog.text || "" }}
      />
      {/* Employees */}
      {blog?.employees && (
        <div className={style.employeeGrid}>
          {blog.employees.map((employee, index) => (
            <div
              key={index}
              className={`${style.card} ${inView ? style.fadeIn : ''}`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <img src={employee.image} alt={employee.name} />
              <h4>{employee.name}</h4>
              <p className={style.position}>{employee.position}</p>
              <div
                className={style.description}
                dangerouslySetInnerHTML={{
                  __html: employee.caption || ""
                }}
              />            
              </div>
          ))}
        </div>
      )}

      {/* Gallery Grid */}
      {Array.isArray(blog.galleryImages) && blog.galleryImages.length > 0 && (
        <div className={`${style.galleryGrid} ${inView ? style.galleryVisible : ''}`}>
          {blog.galleryImages.map((img, index) => (
            <img
              key={index}
              src={img}
              alt={`Gallery item ${index + 1}`}
              className={style.galleryItem}
              onClick={() => openLightboxAt(index)}
              style={{ '--delay': index }}
              loading="lazy"
            />
          ))}
        </div>
      )}

      {/* Swiper Lightbox */}
      {isLightboxOpen && (
        <div className={style.lbOverlay} onClick={() => setIsLightboxOpen(false)}>
          <div className={style.lbInner} onClick={(e) => e.stopPropagation()}>
            <Swiper
              modules={[Navigation, Pagination, Keyboard, Zoom]}
              initialSlide={startIndex}
              navigation
              pagination={{ clickable: true }}
              keyboard={{ enabled: true }}
              zoom={{ maxRatio: 2 }}
              className={style.lbSwiper}
            >
              {blog.galleryImages.map((src, i) => (
                <SwiperSlide key={i}>
                  <div className="swiper-zoom-container">
                    <img src={src} alt={`Slide ${i + 1}`} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Close button */}
            <button
              type="button"
              className={style.lbClose}
              aria-label="Close"
              onClick={() => setIsLightboxOpen(false)}
              title="Close (Esc)"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default BlogsDetail;
